/**
 * Stage 4: assemble the FloorPlanRoom skeleton from the chosen primary rect.
 *
 * Converts from the working frame (feet, y-up, raw origin) to room coordinates
 * (back edge at y=0, y grows toward the front, i.e. raw north becomes the back
 * edge), then turns protrusions into alcoves (with outer-corner chamfers),
 * corner-triangle intrusions into room chamfers, and remaining intrusions into
 * free wall elements.
 */

import { emptyRoomStructure, makeAlcove, makeCorner, makeFreeElement } from '../edits';
import { edgeFrame, lineCoords } from '../geometry';
import { CompleteFloorPlanRoom, CornerName, EDGE_NAMES, EdgeName, FloorPlanCorner, FloorPlanFreeElement, Point } from '../schemas';
import { ConversionReport } from './conversionTypes';
import { PrimaryRect, PrimaryRectChoice, ResidualComponent } from './primaryRect';

const DIAG_TOL = 0.05;

/** Neighbouring slices of a protrusion this close in depth (ft) are one alcove. */
const LIKE_DEPTH = 0.1;

/** Working frame (y-up) → room frame (back edge at y=0, y toward front). */
export const makeToRoom = (r: PrimaryRect) => (p: Point): Point => ({ x: p.x - r.xL, y: r.yB - p.y });

const SIDE_TO_EDGE: Record<'west' | 'north' | 'east' | 'south', EdgeName> = {
  north: 'backEdge',
  south: 'frontEdge',
  west: 'leftEdge',
  east: 'rightEdge',
};

/** A point in an edge's frame: `along` the edge from its start, `out` beyond it. */
interface EdgePoint {
  along: number;
  out: number;
}

/** A stretch [from, to] along the edge that a protrusion fills to `depth`. */
interface DepthRun {
  from: number;
  to: number;
  depth: number;
}

/** How far out the protrusion's boundary reaches at `along`: the furthest any of its ring's edges reaches there. */
function depthAt(ring: EdgePoint[], along: number): number {
  let max = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    if (Math.min(a.along, b.along) > along || Math.max(a.along, b.along) < along || Math.abs(b.along - a.along) < 1e-9) continue;
    max = Math.max(max, a.out + ((b.out - a.out) * (along - a.along)) / (b.along - a.along));
  }
  return max;
}

/** Lengthen `run` over the slice [from, to] of like depth, length-weighting its depth. */
function lengthenRun(run: DepthRun, from: number, to: number, depth: number): void {
  run.depth = (run.depth * (run.to - run.from) + depth * (to - from)) / (to - run.from);
  run.to = to;
}

/**
 * Depth-histogram decomposition: slice the protrusion at every vertex, sample
 * the outer boundary depth at each slice's midpoint, merge like-depth
 * neighbours, and emit one alcove per run. Stacked abutting alcoves merge in
 * the resolver (shared walls dissolve, depth steps become risers), so this
 * represents any staircase-shaped protrusion exactly.
 */
function histogramAlcoves(shape: CompleteFloorPlanRoom, edge: EdgeName, ring: EdgePoint[]): void {
  const events = [...new Set(ring.map(p => +p.along.toFixed(3)))].sort((a, b) => a - b);
  const runs: DepthRun[] = [];
  for (let i = 0; i + 1 < events.length; i++) {
    const from = events[i], to = events[i + 1];
    if (to - from < DIAG_TOL) continue;
    const depth = depthAt(ring, (from + to) / 2);
    const previous = runs[runs.length - 1];
    if (previous && Math.abs(previous.depth - depth) < LIKE_DEPTH && Math.abs(previous.to - from) < 1e-6) lengthenRun(previous, from, to, depth);
    else if (depth > DIAG_TOL) runs.push({ from, to, depth });
  }
  for (const run of runs) shape[edge].segments.push(makeAlcove(run.from, run.to - run.from, run.depth));
}

/** The alcove's back corner chamfers, from the protrusion ring's diagonal edges at its outer ends, and the area every diagonal cuts off. */
function outerChamfers(ring: EdgePoint[], from: number, to: number, horizontal: boolean) {
  const corners: { backLeftCorner?: FloorPlanCorner; backRightCorner?: FloorPlanCorner } = {};
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const ds = Math.abs(b.along - a.along), dOut = Math.abs(b.out - a.out);
    if (ds < DIAG_TOL || dOut < DIAG_TOL) continue;
    const corner: FloorPlanCorner = horizontal ? { dx: ds, dy: dOut } : { dx: dOut, dy: ds };
    if (Math.min(a.along, b.along) - from < DIAG_TOL) corners.backLeftCorner = corner;
    else if (to - Math.max(a.along, b.along) < DIAG_TOL) corners.backRightCorner = corner;
    area += (ds * dOut) / 2;
  }
  return { corners, area };
}

/**
 * The protrusion beyond `edge` as alcoves: one, with chamfered back corners,
 * when that reproduces the scanned area; otherwise the protrusion is
 * staircase-shaped and becomes stacked alcoves, one per depth.
 */
function addAlcove(shape: CompleteFloorPlanRoom, edge: EdgeName, comp: ResidualComponent, toRoom: (p: Point) => Point): void {
  const frame = edgeFrame(edge, shape.width, shape.length);
  const ring = comp.ring.map(toRoom).map(point => lineCoords(frame, point));
  const from = Math.min(...ring.map(p => p.along)), to = Math.max(...ring.map(p => p.along));
  const depth = Math.max(...ring.map(p => p.out));
  const chamfers = outerChamfers(ring, from, to, frame.horizontal);
  if (Math.abs((to - from) * depth - chamfers.area - comp.area) <= 0.25) {
    shape[edge].segments.push({ ...makeAlcove(from, to - from, depth), ...chamfers.corners });
  } else histogramAlcoves(shape, edge, ring);
}

function addCornerChamfer(shape: CompleteFloorPlanRoom, comp: ResidualComponent, r: PrimaryRect, report: ConversionReport): void {
  const { bbox } = comp;
  const midX = (bbox.x0 + bbox.x1) / 2 < (r.xL + r.xR) / 2 ? 'L' : 'R';
  const midY = (bbox.y0 + bbox.y1) / 2 < (r.yF + r.yB) / 2 ? 'F' : 'B';
  const corner: CornerName = midY === 'B'
    ? (midX === 'L' ? 'backLeftCorner' : 'backRightCorner')
    : (midX === 'L' ? 'frontLeftCorner' : 'frontRightCorner');
  if (shape[corner].dx || shape[corner].dy) {
    report.warnings.push(`Duplicate chamfer at ${corner} — keeping the larger`);
    if ((bbox.x1 - bbox.x0) * (bbox.y1 - bbox.y0) < shape[corner].dx * shape[corner].dy) return;
  }
  shape[corner] = makeCorner(bbox.x1 - bbox.x0, bbox.y1 - bbox.y0);
}

/** A free wall element filling an intrusion's bounding box, placed by its back-left corner in the room frame. */
function intrusionWall(comp: ResidualComponent, toRoom: (p: Point) => Point): FloorPlanFreeElement {
  const backLeft = toRoom({ x: comp.bbox.x0, y: comp.bbox.y1 });
  return makeFreeElement('wall', backLeft.x, backLeft.y, comp.bbox.x1 - comp.bbox.x0, comp.bbox.y1 - comp.bbox.y0);
}

/** Build the room skeleton (no doors/windows/objects yet) in room coordinates.
 *  Intake works on a fully-materialized room (every optional structural field
 *  present) so the assembly stages can mutate it in place. */
export function buildRoomShape(
  choice: PrimaryRectChoice,
  heightFt: number | undefined,
  report: ConversionReport,
): CompleteFloorPlanRoom {
  const { rect } = choice;
  const toRoom = makeToRoom(rect);
  const shape: CompleteFloorPlanRoom = { width: rect.xR - rect.xL, length: rect.yB - rect.yF, height: heightFt, ...emptyRoomStructure() };

  for (const { side, comp } of choice.protrusions) addAlcove(shape, SIDE_TO_EDGE[side], comp, toRoom);
  for (const edge of EDGE_NAMES) shape[edge].segments.sort((a, b) => a.position - b.position);

  for (const { kind, comp } of choice.intrusions) {
    if (kind === 'cornerTriangle') {
      addCornerChamfer(shape, comp, rect, report);
      continue;
    }
    if (kind === 'other') report.warnings.push('Non-rectangular room intrusion approximated by its bounding box as a wall element');
    shape.freeElements.push(intrusionWall(comp, toRoom));
  }
  return shape;
}
